import {
  ApplicationIcon,
  Button,
  Icon,
  Illustration,
  type IllustrationKind,
  SkeletonLoader,
  Typography,
} from '@saviynt/design-system';
import { FormattedMessage } from 'react-intl';

import { TOKENS } from '../../constants';
import { getModelLogo } from '../../utils/utilManagepage';

interface InitialLoadingProps {
  count?: number;
}

/**
 * @description Full-page skeleton loader shown during the initial agent list fetch.
 * @param props - {@link InitialLoadingProps}
 * @param props.count - Number of header skeleton bars to render. Defaults to `4`.
 * @returns A skeleton placeholder layout.
 */
export function InitialLoading({ count = 4 }: InitialLoadingProps) {
  return (
    <div className='InitialLoadingWrapper'>
      <div className='InitialLoadingWrapper-headerSkeletons'>
        {Array.from({ length: count }, () => (
          <SkeletonLoader
            shape='square'
            animationStyle='pulse'
            height='100%'
            width='100%'
          />
        ))}
      </div>
      <SkeletonLoader
        shape='square'
        animationStyle='pulse'
        height='5rem'
        width='100%'
      />
      <SkeletonLoader
        shape='square'
        animationStyle='pulse'
        height='100%'
        width='100%'
      />
    </div>
  );
}

interface InitialErrorProps {
  onRefresh: () => void;
}

/**
 * @description Full-page error state with an illustration and a Refresh button, shown when the initial agent list fetch fails.
 * @param props - {@link InitialErrorProps}
 * @param props.onRefresh - Callback invoked when the user clicks Refresh.
 * @returns An error illustration with a Refresh button.
 */
export function InitialError({ onRefresh }: InitialErrorProps) {
  return (
    <div className='InitialErrorWrapper'>
      <Illustration kind={'Error' as IllustrationKind} />
      <div className='InitialErrorWrapper-text'>
        <Typography kind='h1' color={TOKENS.CF_NEU_BLD}>
          <FormattedMessage
            id='CCJcFO'
            defaultMessage='Something went wrong while loading the page.'
            description='went wrong title'
          />
        </Typography>
        <Typography kind='body1' color={TOKENS.CF_NEU_BLD}>
          <FormattedMessage
            id='x23s1M'
            defaultMessage='Try refreshing, or check back later. If the issue persists, contact your administrator.'
            description='went wrong sub-title'
          />
        </Typography>
      </div>
      <Button kind='filled' onClick={onRefresh}>
        <FormattedMessage
          id='NURyBn'
          defaultMessage='Refresh'
          description='refresh'
        />
      </Button>
    </div>
  );
}

interface EmptyListProps {
  onNewSetUp: () => void;
}

/**
 * @description Empty state shown when no agents exist yet, prompting the user to set up integrations.
 * @param props - {@link EmptyListProps}
 * @param props.onNewSetUp - Callback invoked when the user clicks "Set Up Integrations".
 * @returns An empty-state illustration with a setup button.
 */
export function EmptyList({ onNewSetUp }: EmptyListProps) {
  return (
    <div className='EmptyListWrapper'>
      <Illustration kind={'Empty' as IllustrationKind} />
      <Typography kind='h1' color={TOKENS.CF_NEU_BLD}>
        <FormattedMessage
          id='Wu1BAD'
          defaultMessage='No Agents yet'
          description='no agents message'
        />
      </Typography>
      <Typography kind='body1' color={TOKENS.CF_NEU_MED}>
        <FormattedMessage
          id='rgjeiz'
          defaultMessage='Agents are discovered automatically once you connect your AI platforms. Set up an integration to get started.'
          description='no agents message'
        />
      </Typography>
      <Button
        kind='filled'
        size='large'
        rightIcon={<Icon kind='JumpLink' />}
        onClick={onNewSetUp}>
        <FormattedMessage
          id='QTJtjY'
          defaultMessage='Set Up Integrations'
          description='no agents message'
        />
      </Button>
    </div>
  );
}

/**
 * @description Empty state shown when the current search/filter combination returns no agents. Suggests updating filters or clearing the search.
 * @returns An empty-state illustration with filter guidance text.
 */
export function EmptyListWithFilters() {
  return (
    <div className='EmptyListWithFiltersWrapper'>
      <Illustration kind={'Empty' as IllustrationKind} />
      <div className='EmptyListWithFiltersWrapper-text'>
        <Typography kind='h1' color={TOKENS.CF_NEU_BLD}>
          <FormattedMessage
            id='5gddB1'
            defaultMessage='No Matching Results Found'
            description='no match title'
          />
        </Typography>
        <Typography kind='body1' color={TOKENS.CF_NEU_BLD}>
          <FormattedMessage
            id='9olGwD'
            defaultMessage='Try updating the filters, clearing search or check back later.'
            description='no match sub-title'
          />
        </Typography>
      </div>
    </div>
  );
}

/**
 * @description Skeleton loader appended below the list while the next page of agents is being fetched (infinite scroll).
 * @returns A set of skeleton rows for the next page.
 */
export function NextPageLoader() {
  return (
    <div className='NextPageLoaderWrapper'>
      {Array.from({ length: 3 }, () => (
        <SkeletonLoader
          shape='square'
          animationStyle='wave'
          height='4rem'
          width='100%'
        />
      ))}
    </div>
  );
}

interface NextPageErrorProps {
  onNextRefresh: () => void;
}

/**
 * @description Inline error state shown when a subsequent page fetch fails during infinite scroll. Displays an error illustration with a Refresh button.
 * @param props - {@link NextPageErrorProps}
 * @param props.onNextRefresh - Callback invoked when the user clicks Refresh.
 * @returns An inline error illustration with a Refresh button.
 */
export function NextPageError({ onNextRefresh }: NextPageErrorProps) {
  return (
    <div className='NextPageErrorWrapper'>
      <Illustration kind={'Error' as IllustrationKind} />
      <Typography kind='h1' color={TOKENS.CF_NEU_BLD}>
        <FormattedMessage
          id='CCJcFO'
          defaultMessage='Something went wrong while loading the page.'
          description='went wrong title'
        />
      </Typography>
      <Typography kind='body1' color={TOKENS.CF_NEU_BLD}>
        <FormattedMessage
          id='x23s1M'
          defaultMessage='Try refreshing, or check back later. If the issue persists, contact your administrator.'
          description='went wrong sub-title'
        />
      </Typography>
      <Button type='button' kind='filled' onClick={onNextRefresh}>
        <FormattedMessage
          id='NURyBn'
          defaultMessage='Refresh'
          description='refresh'
        />
      </Button>
    </div>
  );
}

/**
 * @description Renders the Google Gemini brand icon as an inline SVG. Used as a custom model logo
 * when the resolved model icon is "GoogleGemini" and the design-system `ApplicationIcon` does not
 * provide a built-in variant.
 * @returns The Google Gemini SVG icon as a styled `svg` element.
 */
export function GoogleGeminiSvg() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='48'
      height='48'
      viewBox='0 0 48 48'
      fill='none'
      style={{
        background: 'var(--color-background-neutral-subtle)',
        borderRadius: 'var(--border-radius-circle)',
        width: '1.5rem',
        height: '1.5rem',
      }}>
      <rect width='48' height='48' rx='24' fill='white' />
      <mask
        id='mask0_38401_1510'
        maskUnits='userSpaceOnUse'
        x='6'
        y='6'
        width='36'
        height='36'>
        <path
          d='M24 6C24.3772 6 24.7062 6.25796 24.7983 6.62409C25.0797 7.74394 25.4506 8.83939 25.9072 9.8999C27.1011 12.6737 28.7392 15.1012 30.819 17.181C32.8999 19.2613 35.3269 20.8995 38.1001 22.0933C39.1609 22.5499 40.2565 22.9207 41.3765 23.2023C41.7426 23.2944 42 23.6228 42 24C42 24.3772 41.7426 24.7062 41.3759 24.7983C40.2561 25.0797 39.1606 25.4505 38.1001 25.9072C35.3263 27.1011 32.8993 28.7392 30.819 30.819C28.7392 32.8999 27.1011 35.3269 25.9072 38.1001C25.4505 39.1608 25.0795 40.2565 24.7977 41.3765C24.7531 41.5544 24.6505 41.7123 24.506 41.8253C24.3615 41.9382 24.1834 41.9997 24 42C23.6228 42 23.2944 41.7426 23.2023 41.3759C22.9206 40.2561 22.5496 39.1606 22.0928 38.1001C20.8995 35.3263 19.2619 32.8993 17.181 30.819C15.1007 28.7392 12.6737 27.1011 9.8999 25.9072C8.83934 25.4505 7.74391 25.0795 6.62409 24.7977C6.44618 24.7533 6.28818 24.6507 6.17514 24.5063C6.06209 24.3619 6.00046 24.1839 6 24.0006C6 23.6233 6.25796 23.2949 6.62409 23.2028C7.74395 22.9212 8.8394 22.5502 9.8999 22.0933C12.6737 20.9001 15.1012 19.2619 17.181 17.1816C19.2613 15.1018 20.8995 12.6742 22.0933 9.90045C22.5499 8.83988 22.9207 7.74445 23.2023 6.62465C23.2466 6.44654 23.3492 6.28835 23.4938 6.17519C23.6383 6.06202 23.8164 6.00037 24 6Z'
          fill='black'
        />
        <path
          d='M24 6C24.3772 6 24.7062 6.25796 24.7983 6.62409C25.0797 7.74394 25.4506 8.83939 25.9072 9.8999C27.1011 12.6737 28.7392 15.1012 30.819 17.181C32.8999 19.2613 35.3269 20.8995 38.1001 22.0933C39.1609 22.5499 40.2565 22.9207 41.3765 23.2023C41.7426 23.2944 42 23.6228 42 24C42 24.3772 41.7426 24.7062 41.3759 24.7983C40.2561 25.0797 39.1606 25.4505 38.1001 25.9072C35.3263 27.1011 32.8993 28.7392 30.819 30.819C28.7392 32.8999 27.1011 35.3269 25.9072 38.1001C25.4505 39.1608 25.0795 40.2565 24.7977 41.3765C24.7531 41.5544 24.6505 41.7123 24.506 41.8253C24.3615 41.9382 24.1834 41.9997 24 42C23.6228 42 23.2944 41.7426 23.2023 41.3759C22.9206 40.2561 22.5496 39.1606 22.0928 38.1001C20.8995 35.3263 19.2619 32.8993 17.181 30.819C15.1007 28.7392 12.6737 27.1011 9.8999 25.9072C8.83934 25.4505 7.74391 25.0795 6.62409 24.7977C6.44618 24.7533 6.28818 24.6507 6.17514 24.5063C6.06209 24.3619 6.00046 24.1839 6 24.0006C6 23.6233 6.25796 23.2949 6.62409 23.2028C7.74395 22.9212 8.8394 22.5502 9.8999 22.0933C12.6737 20.9001 15.1012 19.2619 17.181 17.1816C19.2613 15.1018 20.8995 12.6742 22.0933 9.90045C22.5499 8.83988 22.9207 7.74445 23.2023 6.62465C23.2466 6.44654 23.3492 6.28835 23.4938 6.17519C23.6383 6.06202 23.8164 6.00037 24 6Z'
          fill='url(#paint0_linear_38401_1510)'
        />
      </mask>
      <g mask='url(#mask0_38401_1510)'>
        <g filter='url(#filter0_f_38401_1510)'>
          <path
            d='M2.74858 34.1453C6.9081 35.6226 11.6889 32.8527 13.427 27.9587C15.165 23.0653 13.2017 17.9005 9.04223 16.4232C4.88271 14.9459 0.101866 17.7158 -1.63672 22.6093C-3.3742 27.5033 -1.41094 32.668 2.74858 34.1453Z'
            fill='#FFE432'
          />
        </g>
        <g filter='url(#filter1_f_38401_1510)'>
          <path
            d='M21.2184 18.0102C26.9323 18.0102 31.565 13.2754 31.565 7.4355C31.565 1.59508 26.9329 -3.13916 21.2184 -3.13916C15.5039 -3.13916 10.8706 1.59564 10.8706 7.4355C10.8706 13.2754 15.5033 18.0102 21.2184 18.0102Z'
            fill='#FC413D'
          />
        </g>
        <g filter='url(#filter2_f_38401_1510)'>
          <path
            d='M17.1979 51.8276C23.1632 51.5363 27.6927 45.0352 27.3149 37.3075C26.9377 29.5798 21.7952 23.5514 15.8299 23.8432C9.86469 24.135 5.33515 30.6355 5.71293 38.3632C6.09072 46.0909 11.2327 52.1194 17.1979 51.8276Z'
            fill='#00B95C'
          />
        </g>
        <g filter='url(#filter3_f_38401_1510)'>
          <path
            d='M17.1979 51.8276C23.1632 51.5363 27.6927 45.0352 27.3149 37.3075C26.9377 29.5798 21.7952 23.5514 15.8299 23.8432C9.86469 24.135 5.33515 30.6355 5.71293 38.3632C6.09072 46.0909 11.2327 52.1194 17.1979 51.8276Z'
            fill='#00B95C'
          />
        </g>
        <g filter='url(#filter4_f_38401_1510)'>
          <path
            d='M23.171 47.1521C28.1716 44.1093 29.5102 37.1799 26.1606 31.6746C22.811 26.1687 16.0414 24.1721 11.0403 27.2144C6.03922 30.2577 4.70061 37.1871 8.05019 42.693C11.4009 48.1984 18.17 50.1949 23.171 47.1521Z'
            fill='#00B95C'
          />
        </g>
        <g filter='url(#filter5_f_38401_1510)'>
          <path
            d='M43.385 29.85C49.0057 29.85 53.5624 25.462 53.5624 20.0498C53.5624 14.6371 49.0057 10.249 43.385 10.249C37.7642 10.249 33.2075 14.6371 33.2075 20.0498C33.2075 25.4625 37.7642 29.85 43.385 29.85Z'
            fill='#3186FF'
          />
        </g>
        <g filter='url(#filter6_f_38401_1510)'>
          <path
            d='M-1.24819 28.7129C3.92763 32.6483 11.4883 31.414 15.6395 25.9547C19.7907 20.496 18.9608 12.8798 13.785 8.94438C8.60917 5.00842 1.04903 6.24274 -3.10273 11.702C-7.25393 17.1608 -6.42346 24.7775 -1.24819 28.7129Z'
            fill='#FBBC04'
          />
        </g>
        <g filter='url(#filter7_f_38401_1510)'>
          <path
            d='M25.2713 34.5316C31.4485 38.7787 39.6372 37.596 43.5604 31.8887C47.4841 26.182 45.6568 18.1132 39.4791 13.866C33.3013 9.6177 25.1132 10.8015 21.1895 16.5077C17.2663 22.215 19.0931 30.2838 25.2708 34.5316H25.2713Z'
            fill='#3186FF'
          />
        </g>
        <g filter='url(#filter8_f_38401_1510)'>
          <path
            d='M36.5023 4.70365C38.0739 6.84055 36.054 10.9945 31.9916 13.983C27.9286 16.9714 23.3613 17.6615 21.7897 15.5252C20.2181 13.3877 22.2374 9.23319 26.2998 6.2453C30.3628 3.25686 34.9307 2.56675 36.5017 4.7031L36.5023 4.70365Z'
            fill='#749BFF'
          />
        </g>
        <g filter='url(#filter9_f_38401_1510)'>
          <path
            d='M23.6004 14.934C29.8835 9.10576 32.0399 1.21499 28.4173 -2.69046C24.7948 -6.5959 16.7642 -5.03816 10.4811 0.790051C4.19803 6.61826 2.04116 14.509 5.66424 18.4145C9.28676 22.3199 17.3173 20.7622 23.6004 14.934Z'
            fill='#FC413D'
          />
        </g>
        <g filter='url(#filter10_f_38401_1510)'>
          <path
            d='M10.7209 35.866C14.4554 38.5388 18.7426 38.9449 20.297 36.7736C21.8514 34.6017 20.0839 30.6747 16.3494 28.0019C12.6153 25.3291 8.32766 24.923 6.77381 27.0943C5.21939 29.2661 6.98628 33.1932 10.7209 35.866Z'
            fill='#FFEE48'
          />
        </g>
      </g>
      <defs>
        <filter
          id='filter0_f_38401_1510'
          x='-7.18953'
          y='11.1059'
          width='26.1696'
          height='28.3566'
          filterUnits='userSpaceOnUse'
          colorInterpolationFilters='sRGB'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feBlend
            mode='normal'
            in='SourceGraphic'
            in2='BackgroundImageFix'
            result='shape'
          />
          <feGaussianBlur
            stdDeviation='2.46'
            result='effect1_foregroundBlur_38401_1510'
          />
        </filter>
        <filter
          id='filter1_f_38401_1510'
          x='-12.9114'
          y='-26.9212'
          width='68.2583'
          height='68.7134'
          filterUnits='userSpaceOnUse'
          colorInterpolationFilters='sRGB'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feBlend
            mode='normal'
            in='SourceGraphic'
            in2='BackgroundImageFix'
            result='shape'
          />
          <feGaussianBlur
            stdDeviation='11.891'
            result='effect1_foregroundBlur_38401_1510'
          />
        </filter>
        <filter
          id='filter2_f_38401_1510'
          x='-14.5271'
          y='3.61501'
          width='62.082'
          height='68.4409'
          filterUnits='userSpaceOnUse'
          colorInterpolationFilters='sRGB'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feBlend
            mode='normal'
            in='SourceGraphic'
            in2='BackgroundImageFix'
            result='shape'
          />
          <feGaussianBlur
            stdDeviation='10.109'
            result='effect1_foregroundBlur_38401_1510'
          />
        </filter>
        <filter
          id='filter3_f_38401_1510'
          x='-14.5271'
          y='3.61501'
          width='62.082'
          height='68.4409'
          filterUnits='userSpaceOnUse'
          colorInterpolationFilters='sRGB'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feBlend
            mode='normal'
            in='SourceGraphic'
            in2='BackgroundImageFix'
            result='shape'
          />
          <feGaussianBlur
            stdDeviation='10.109'
            result='effect1_foregroundBlur_38401_1510'
          />
        </filter>
        <filter
          id='filter4_f_38401_1510'
          x='-14.0124'
          y='5.5735'
          width='62.2358'
          height='63.2197'
          filterUnits='userSpaceOnUse'
          colorInterpolationFilters='sRGB'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feBlend
            mode='normal'
            in='SourceGraphic'
            in2='BackgroundImageFix'
            result='shape'
          />
          <feGaussianBlur
            stdDeviation='10.109'
            result='effect1_foregroundBlur_38401_1510'
          />
        </filter>
        <filter
          id='filter5_f_38401_1510'
          x='13.9955'
          y='-8.96298'
          width='58.779'
          height='58.0251'
          filterUnits='userSpaceOnUse'
          colorInterpolationFilters='sRGB'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feBlend
            mode='normal'
            in='SourceGraphic'
            in2='BackgroundImageFix'
            result='shape'
          />
          <feGaussianBlur
            stdDeviation='9.606'
            result='effect1_foregroundBlur_38401_1510'
          />
        </filter>
        <filter
          id='filter6_f_38401_1510'
          x='-23.1571'
          y='-10.7699'
          width='58.8513'
          height='59.1966'
          filterUnits='userSpaceOnUse'
          colorInterpolationFilters='sRGB'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feBlend
            mode='normal'
            in='SourceGraphic'
            in2='BackgroundImageFix'
            result='shape'
          />
          <feGaussianBlur
            stdDeviation='8.706'
            result='effect1_foregroundBlur_38401_1510'
          />
        </filter>
        <filter
          id='filter7_f_38401_1510'
          x='3.57256'
          y='-4.23311'
          width='57.6049'
          height='56.8632'
          filterUnits='userSpaceOnUse'
          colorInterpolationFilters='sRGB'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feBlend
            mode='normal'
            in='SourceGraphic'
            in2='BackgroundImageFix'
            result='shape'
          />
          <feGaussianBlur
            stdDeviation='7.775'
            result='effect1_foregroundBlur_38401_1510'
          />
        </filter>
        <filter
          id='filter8_f_38401_1510'
          x='7.34235'
          y='-10.4521'
          width='43.6073'
          height='41.1322'
          filterUnits='userSpaceOnUse'
          colorInterpolationFilters='sRGB'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feBlend
            mode='normal'
            in='SourceGraphic'
            in2='BackgroundImageFix'
            result='shape'
          />
          <feGaussianBlur
            stdDeviation='6.957'
            result='effect1_foregroundBlur_38401_1510'
          />
        </filter>
        <filter
          id='filter9_f_38401_1510'
          x='-7.84575'
          y='-16.5943'
          width='49.773'
          height='48.9127'
          filterUnits='userSpaceOnUse'
          colorInterpolationFilters='sRGB'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feBlend
            mode='normal'
            in='SourceGraphic'
            in2='BackgroundImageFix'
            result='shape'
          />
          <feGaussianBlur
            stdDeviation='5.876'
            result='effect1_foregroundBlur_38401_1510'
          />
        </filter>
        <filter
          id='filter10_f_38401_1510'
          x='-8.33653'
          y='11.1522'
          width='43.7439'
          height='41.5632'
          filterUnits='userSpaceOnUse'
          colorInterpolationFilters='sRGB'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feBlend
            mode='normal'
            in='SourceGraphic'
            in2='BackgroundImageFix'
            result='shape'
          />
          <feGaussianBlur
            stdDeviation='7.273'
            result='effect1_foregroundBlur_38401_1510'
          />
        </filter>
        <linearGradient
          id='paint0_linear_38401_1510'
          x1='16.2335'
          y1='30.0873'
          x2='34.9319'
          y2='14.3235'
          gradientUnits='userSpaceOnUse'>
          <stop stopColor='#4893FC' />
          <stop offset='0.27' stopColor='#4893FC' />
          <stop offset='0.777' stopColor='#969DFF' />
          <stop offset='1' stopColor='#BD99FE' />
        </linearGradient>
      </defs>
    </svg>
  );
}

interface LlmModelIconProps {
  model?: string | null;
}

/**
 * @description Renders the LLM model icon for an agent row. Returns `null`
 * when no model is provided. Resolves the model logo via {@link getModelLogo} and includes
 * special-case handling for the GoogleGemini model.
 * @param props - {@link LlmModelIconProps}
 * @param props.model - The LLM model name, or `null`/`undefined` if unavailable.
 * @returns `null` when no model is set, a {@link GoogleGeminiSvg} for Gemini models, or an {@link ApplicationIcon} for other models.
 */
export function LlmModelIcon({ model }: LlmModelIconProps) {
  if (!model) {
    return null;
  }

  const modelIcon = getModelLogo(model);

  if (modelIcon === 'GoogleGemini') {
    return <GoogleGeminiSvg />;
  }

  return <ApplicationIcon logo={modelIcon} size='xSmall' />;
}
